/**
 * TK-41 (lee 24/10, thiết kế NEMA_MESH_V02 + quy ước mesh nháp 2) — tab Cài đặt ban đầu: API + luật thuần
 * (bộ kiểm nạp hàm thuần qua bản chép đúng đoạn — giữ đoạn giữa hai dấu `export function doiChieu` … `export const qkCaiDatBanDau`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { NodeXep, ToaDo } from './xepViTri';

export interface HangNode { addr: number; parentLuu: number | null; parentLuot: number | null; thayMs: number | null }
export type NhanDoiChieu = 'moi' | 'doi-lien-ket' | 'chua-thay' | 'da-luu';

/**
 * Đối chiếu một hàng với BẢNG ĐÃ LƯU (phiếu §4):
 *   Node mới — có ở lượt này, không có trong bảng đã lưu · Đổi liên kết — đã lưu, lượt này báo cha KHÁC cha đã lưu ·
 *   Đã lưu · Chưa thấy lượt này — đã lưu, đang có lượt dò, lượt này chưa báo · Đã lưu — còn lại. Thuần.
 */
export function doiChieu(h: HangNode, coLuot: boolean): NhanDoiChieu {
  if (h.parentLuu === null) return 'moi';
  if (h.parentLuot !== null && h.parentLuot !== h.parentLuu) return 'doi-lien-ket';
  if (coLuot && h.parentLuot === null) return 'chua-thay';
  return 'da-luu';
}
/** ID node 4 chữ số hex "0xA101" (ID gốc / ID liên kết là thông tin kỹ thuật lee đặt hàng cho bảng này). Thuần. */
/**
 * TK-44 §3 (lee 24/10): chữ hai ô mesh — MỘT chỗ. Giá trị do TỦ BÁO (CabinetState 18/19, máy chủ ghi `nemaHostBat` / `meshKichHoat`).
 *   Tủ mất kết nối ⇒ "Mất kết nối" XÁM; tủ không khai (null, firmware cũ) ⇒ "—" xám. KHÔNG còn "Chưa biết" ở trang này.
 */
export function chuMesh(online: boolean | 'cho-bao', v: boolean | null, loai: 'cai' | 'khoi-dong'): { chu: string; mau: 'tot' | 'canh' | 'thuong' | 'xam' } {
  if (!online) return { chu: 'Mất kết nối', mau: 'xam' };
  /* TK-45 §7 (M2): tủ vừa nối lại, chưa gửi khung trạng thái đầu của phiên ⇒ giá trị trong CSDL là số cũ, không vẽ nó */
  if (online === 'cho-bao') return { chu: 'Đang chờ tủ báo', mau: 'xam' };
  if (v === null) return { chu: '—', mau: 'xam' };
  if (loai === 'cai') return v ? { chu: 'Đang mở', mau: 'tot' } : { chu: 'Đang tắt', mau: 'thuong' };
  return v ? { chu: 'Đã kích hoạt', mau: 'tot' } : { chu: 'Chưa kích hoạt', mau: 'canh' };
}
/** TK-45 §7 (M2): ba trạng thái nối của tủ cho hai ô mesh — MỘT chỗ. */
export const ketNoiMesh = (tu: { online: boolean; choTuBao: boolean }): boolean | 'cho-bao' => (!tu.online ? false : tu.choTuBao ? 'cho-bao' : true);
export const LOP_MAU_MESH = { tot: 'text-tot', canh: 'text-canh', thuong: 'text-chu-2', xam: 'text-chu-3' } as const;
export const hex4 = (n: number) => `0x${n.toString(16).toUpperCase().padStart(4, '0')}`;
/** "Tìm được lúc": giây 2 số lẻ, dấu phẩy ("0,41 s"); null ⇒ "—". Thuần. */
export const giayHai = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(2).replace('.', ',')} s`);
/** Thời gian host đã mở: "45 s" · "3 ph 07 s". Thuần. */
export function demMo(giay: number): string {
  const s = Math.max(0, Math.floor(giay));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} ph ${String(s % 60).padStart(2, '0')} s`;
}
/** Danh sách gửi khi Lưu bảng: mọi hàng không bị loại, cha = cha lượt này (nếu có) hoặc cha đã lưu. Thuần. */
export function dsLuu(bang: HangNode[], loai: ReadonlySet<number>): { addr: number; parent: number }[] {
  return bang.filter((h) => !loai.has(h.addr)).map((h) => ({ addr: h.addr, parent: h.parentLuot ?? h.parentLuu ?? 0 }));
}

export const qkCaiDatBanDau = { cay: ['cai-dat-ban-dau', 'cay'] as const, tu: (id: string) => ['cai-dat-ban-dau', 'tu', id] as const };

export interface TuCaiDatCay { id: string; name: string; code: string | null; online: boolean; soDenLuu: number; meshKichHoat: boolean | null; tuThuong: boolean }
export interface TuyenCaiDat { id: string; tenDayDu: string; tu: TuCaiDatCay[] }
export interface DongNhatKy { luc: string; loai: 'gui' | 've'; chu: string }
export interface TuCaiDat {
  id: string; name: string; code: string | null; hardwareId: string; giaLap: boolean; online: boolean; tuyen: string | null; tuThuong: boolean;
  /** TK-45 §7 (M2): tủ đang nối nhưng chưa gửi khung trạng thái đầu của phiên */
  choTuBao: boolean;
  /** TK-44 §5: toạ độ tủ ("Về tủ"); null = tủ chưa có toạ độ */
  toaDo: ToaDo | null;
  ngoRa: { index: number; isOn: boolean; enabled: boolean; manualHold: boolean }[]; giuTay: boolean;
  hostBat: boolean | null; meshMoLuc: string | null;
  host: { daBao: boolean; statusAt: string | null; state: number | null; kenh: number | null; pan: number | null } | null;
  meshKichHoat: boolean | null; banLuu: number; soDenLuu: number;
  luot: { lan: number; sentAt: string | null } | null;
  /** TK-44 §5: mỗi node kèm tên · vị trí đã đặt · GPS đèn tự báo (xếp vị trí) */
  bang: NodeXep[];
  luuCuoi: { id: string; state: string; ack: { result?: string; detail?: string | null } | null; issuedAt: string } | null;
  nhatKy: DongNhatKy[];
}

export function useCayCaiDat(bat = true) {
  return useQuery({ queryKey: qkCaiDatBanDau.cay, queryFn: () => api.get<TuyenCaiDat[]>('/api/nema/cai-dat/cay'), refetchInterval: 10_000, enabled: bat });
}
export function useTuCaiDat(id: string | null) {
  return useQuery({ queryKey: qkCaiDatBanDau.tu(id ?? ''), queryFn: () => api.get<TuCaiDat>(`/api/nema/cai-dat/tu/${id}`), enabled: !!id, refetchInterval: 1_500 });
}
export type ViecMesh = 'mesh' | 'kich-hoat' | 'do-tim' | 'luu-bang' | 'ngo' | 'tra-quyen';
/** MỘT mutation cho mọi nút của tủ; `dang(v)` = việc v đang gửi (nút ấy hiện "Đang gửi…"). Ngõ / trả quyền đi đường có sẵn. */
export function useViecMesh(id: string | null) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (x: { viec: ViecMesh; than?: unknown }) => {
      if (x.viec === 'ngo') return api.post<unknown>(`/api/cabinets/${id}/control`, x.than);
      if (x.viec === 'tra-quyen') return api.post<unknown>(`/api/cabinets/${id}/release`, {});
      return api.post<unknown>(`/api/nema/cai-dat/tu/${id}/${x.viec}`, x.than ?? {});
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['cai-dat-ban-dau'] }); },
  });
  return { lam: (viec: ViecMesh, than?: unknown) => m.mutateAsync({ viec, than }), dang: (v: ViecMesh) => m.isPending && m.variables?.viec === v, ban: m.isPending };
}

/** TK-44 §5: Lưu bản xếp (tên + vị trí) — chỉ CSDL, không gửi gì xuống tủ. Trùng tên ⇒ máy chủ 409. */
export function useLuuXep(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (den: { addr: number; name?: string | null; lat?: number | null; lng?: number | null }[]) => api.put<{ daLuu: number }>(`/api/nema/cai-dat/tu/${id}/xep`, { den }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['cai-dat-ban-dau'] }); void qc.invalidateQueries({ queryKey: ['nema'] }); },
  });
}
